import type {
  MultiLineString,
  MultiPoint,
  MultiPolygon,
  Point,
  Polygon,
  Position,
} from "geojson";

/** Decoder de WKB (Well-Known Binary) a GeoJSON, sin dependencias — evita
 *  librerías como `wkx` que dependen de `Buffer`/`util` de Node (no hay
 *  polyfill en esta SPA vanilla, ver CLAUDE.md del Server, sección migración
 *  a gRPC-Web). Cubre los 5 tipos de geometría que existen hoy en la base
 *  (`Point`, `Polygon`, `MultiPoint`, `MultiLineString`, `MultiPolygon`; no
 *  `LineString` suelto ni `GeometryCollection`, ninguna columna los usa) —
 *  `LineString` sí se decodifica como caso interno porque así viaja cada
 *  miembro de un `MultiLineString` en el estándar WKB.
 *
 *  Espera WKB estándar de OGC, **sin SRID** (el servidor usa `ST_AsBinary`,
 *  no `ST_AsEWKB` — ver `PostgisFunctions.AsBinary`), 2D (sin Z/M). */

const WKB_TYPE = {
  Point: 1,
  LineString: 2,
  Polygon: 3,
  MultiPoint: 4,
  MultiLineString: 5,
  MultiPolygon: 6,
} as const;

type WkbGeometry =
  | Point
  | { type: "LineString"; coordinates: Position[] }
  | Polygon
  | MultiPoint
  | MultiLineString
  | MultiPolygon;

/** Cursor de lectura sobre el WKB: cada geometría (y cada miembro de una
 *  Multi*) trae su propio byte de orden — se relee en cada nivel, nunca se
 *  asume el del nivel superior. */
class WkbReader {
  private offset = 0;

  constructor(private readonly view: DataView) {}

  private readLittleEndian(): boolean {
    const byteOrder = this.view.getUint8(this.offset);
    this.offset += 1;
    return byteOrder === 1;
  }

  private readUint32(littleEndian: boolean): number {
    const value = this.view.getUint32(this.offset, littleEndian);
    this.offset += 4;
    return value;
  }

  private readPointCoordinates(littleEndian: boolean): Position {
    const x = this.view.getFloat64(this.offset, littleEndian);
    this.offset += 8;
    const y = this.view.getFloat64(this.offset, littleEndian);
    this.offset += 8;
    return [x, y];
  }

  /** Coordenadas crudas de un LineString/anillo: sin header propio (a
   *  diferencia de los miembros de una Multi*, que sí lo llevan). */
  private readLineStringCoordinates(littleEndian: boolean): Position[] {
    const count = this.readUint32(littleEndian);
    const coordinates: Position[] = [];
    for (let i = 0; i < count; i++) {
      coordinates.push(this.readPointCoordinates(littleEndian));
    }
    return coordinates;
  }

  private readPolygonRings(littleEndian: boolean): Position[][] {
    const ringCount = this.readUint32(littleEndian);
    const rings: Position[][] = [];
    for (let i = 0; i < ringCount; i++) {
      rings.push(this.readLineStringCoordinates(littleEndian));
    }
    return rings;
  }

  /** Lee una geometría completa (byte de orden + tipo + cuerpo). Los
   *  miembros de una Multi* son geometrías completas anidadas (con su propio
   *  header) — por eso esto se llama recursivamente, no `readLineString`/
   *  `readPolygon` a secas. */
  readGeometry(): WkbGeometry {
    const littleEndian = this.readLittleEndian();
    const type = this.readUint32(littleEndian);

    switch (type) {
      case WKB_TYPE.Point:
        return { type: "Point", coordinates: this.readPointCoordinates(littleEndian) };

      case WKB_TYPE.LineString:
        return { type: "LineString", coordinates: this.readLineStringCoordinates(littleEndian) };

      case WKB_TYPE.Polygon:
        return { type: "Polygon", coordinates: this.readPolygonRings(littleEndian) };

      case WKB_TYPE.MultiPoint: {
        const count = this.readUint32(littleEndian);
        const coordinates: Position[] = [];
        for (let i = 0; i < count; i++) {
          const member = this.readGeometry();
          if (member.type !== "Point") {
            throw new Error(`WKB MultiPoint con miembro inesperado: ${member.type}`);
          }
          coordinates.push(member.coordinates);
        }
        return { type: "MultiPoint", coordinates };
      }

      case WKB_TYPE.MultiLineString: {
        const count = this.readUint32(littleEndian);
        const coordinates: Position[][] = [];
        for (let i = 0; i < count; i++) {
          const member = this.readGeometry();
          if (member.type !== "LineString") {
            throw new Error(`WKB MultiLineString con miembro inesperado: ${member.type}`);
          }
          coordinates.push(member.coordinates);
        }
        return { type: "MultiLineString", coordinates };
      }

      case WKB_TYPE.MultiPolygon: {
        const count = this.readUint32(littleEndian);
        const coordinates: Position[][][] = [];
        for (let i = 0; i < count; i++) {
          const member = this.readGeometry();
          if (member.type !== "Polygon") {
            throw new Error(`WKB MultiPolygon con miembro inesperado: ${member.type}`);
          }
          coordinates.push(member.coordinates);
        }
        return { type: "MultiPolygon", coordinates };
      }

      default:
        throw new Error(`Tipo de geometría WKB no soportado: ${type}`);
    }
  }
}

/** Decodifica WKB crudo a GeoJSON (une los tipos que existen hoy en la base). */
export function wkbToGeoJSON(bytes: Uint8Array): WkbGeometry {
  return new WkbReader(new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)).readGeometry();
}

/** Variante angosta para campos que se sabe son `Point` (ej. `Finca.ubicacion`). */
export function wkbToPoint(bytes: Uint8Array): Point {
  const geometry = wkbToGeoJSON(bytes);
  if (geometry.type !== "Point") {
    throw new Error(`Se esperaba WKB Point, llegó ${geometry.type}`);
  }
  return geometry;
}

/** Variante angosta para campos que se sabe son `Polygon` (ej.
 *  `Finca.sateliteAreaGeografica`, `Lote.areaGeografica`). */
export function wkbToPolygon(bytes: Uint8Array): Polygon {
  const geometry = wkbToGeoJSON(bytes);
  if (geometry.type !== "Polygon") {
    throw new Error(`Se esperaba WKB Polygon, llegó ${geometry.type}`);
  }
  return geometry;
}

/** Variante angosta para campos que se sabe son `MultiPolygon` (ej.
 *  `Departamento.areaGeografica`, `DepartamentoRegion.areaGeografica` — soportan
 *  territorio disjunto, islas/fragmentos). */
export function wkbToMultiPolygon(bytes: Uint8Array): MultiPolygon {
  const geometry = wkbToGeoJSON(bytes);
  if (geometry.type !== "MultiPolygon") {
    throw new Error(`Se esperaba WKB MultiPolygon, llegó ${geometry.type}`);
  }
  return geometry;
}

/** Variante angosta para campos que se sabe son `MultiLineString` (ej. el
 *  recorrido de una ejecución de tarea de rastro). */
export function wkbToMultiLineString(bytes: Uint8Array): MultiLineString {
  const geometry = wkbToGeoJSON(bytes);
  if (geometry.type !== "MultiLineString") {
    throw new Error(`Se esperaba WKB MultiLineString, llegó ${geometry.type}`);
  }
  return geometry;
}

/** Variante angosta para campos que se sabe son `MultiPoint` (ej. los puntos
 *  de una ejecución de tarea de rastro). */
export function wkbToMultiPoint(bytes: Uint8Array): MultiPoint {
  const geometry = wkbToGeoJSON(bytes);
  if (geometry.type !== "MultiPoint") {
    throw new Error(`Se esperaba WKB MultiPoint, llegó ${geometry.type}`);
  }
  return geometry;
}

/** Codifica un `Polygon` GeoJSON a WKB little-endian, sin SRID — la
 *  contraparte de `wkbToPolygon`, para el sentido cliente → servidor (ej. el
 *  AOI y los lotes que manda el wizard de registro de finca). El servidor lo
 *  lee con `NetTopologySuite.IO.WKBReader`, que soporta cualquier orden de
 *  bytes con solo leer el primer byte — no hace falta que coincida con lo que
 *  emite `ST_AsBinary` en el sentido contrario. Solo `Polygon`: es el único
 *  tipo que este wizard manda (AOI y lotes), a diferencia del decoder de
 *  arriba, que sí cubre los 5 tipos que existen en la base para lo que llega
 *  del servidor. */
/** La línea de corte del paso 4 como WKB `LineString`, little-endian.
 *
 *  Existe porque el corte pasó a resolverse en el servidor: lo que viaja es la
 *  LÍNEA que el usuario trazó, no las dos mitades ya calculadas. Es el mismo
 *  formato y el mismo orden de bytes que `polygonToWkb`, con un anillo menos:
 *  una `LineString` es una lista de puntos sin la vuelta de anillos.
 *
 *  Tira con menos de 2 puntos: una línea de un punto no define nada, y dejarla
 *  pasar haría que el servidor rechazara la petición con un error genérico en
 *  vez de que el cliente sepa que no tiene nada que mandar. */
export function lineStringToWkb(coordinates: readonly Position[]): Uint8Array {
  if (coordinates.length < 2) {
    throw new Error("Una LineString necesita al menos 2 puntos.");
  }

  // byte de orden + tipo + cantidad de puntos + (x,y) float64 por punto
  const buffer = new ArrayBuffer(1 + 4 + 4 + coordinates.length * 16);
  const view = new DataView(buffer);
  let offset = 0;

  view.setUint8(offset, 1); // 1 = little-endian
  offset += 1;
  view.setUint32(offset, WKB_TYPE.LineString, true);
  offset += 4;
  view.setUint32(offset, coordinates.length, true);
  offset += 4;

  for (const [x, y] of coordinates) {
    view.setFloat64(offset, x, true);
    offset += 8;
    view.setFloat64(offset, y, true);
    offset += 8;
  }

  return new Uint8Array(buffer);
}

/** Un vértice como WKB `Point`, little-endian.
 *
 *  Lo usa la edición de vértices del paso 4: un vértice se nombra por su
 *  POSICIÓN y no por un id —no hay tabla de vértices de lote—, así que lo que
 *  viaja al servidor es el punto. */
export function pointToWkb(coordinate: Position): Uint8Array {
  // byte de orden + tipo + (x,y) float64
  const buffer = new ArrayBuffer(1 + 4 + 16);
  const view = new DataView(buffer);

  view.setUint8(0, 1); // 1 = little-endian
  view.setUint32(1, WKB_TYPE.Point, true);
  view.setFloat64(5, coordinate[0], true);
  view.setFloat64(13, coordinate[1], true);

  return new Uint8Array(buffer);
}

export function polygonToWkb(polygon: Polygon): Uint8Array {
  let byteLength = 1 + 4 + 4; // byte de orden + tipo + cantidad de anillos
  for (const ring of polygon.coordinates) {
    byteLength += 4 + ring.length * 16; // cantidad de puntos + (x,y) float64
  }

  const buffer = new ArrayBuffer(byteLength);
  const view = new DataView(buffer);
  let offset = 0;

  view.setUint8(offset, 1); // 1 = little-endian
  offset += 1;
  view.setUint32(offset, WKB_TYPE.Polygon, true);
  offset += 4;
  view.setUint32(offset, polygon.coordinates.length, true);
  offset += 4;

  for (const ring of polygon.coordinates) {
    view.setUint32(offset, ring.length, true);
    offset += 4;
    for (const [x, y] of ring) {
      view.setFloat64(offset, x, true);
      offset += 8;
      view.setFloat64(offset, y, true);
      offset += 8;
    }
  }

  return new Uint8Array(buffer);
}
